/**
 * Series de facturación.
 *
 * Esta pantalla existe por una razón de plazo, no de comodidad.
 *
 * La comprobación de preparación avisa de que «no hay ninguna serie de
 * facturación» y dice que sin ella no se puede emitir la primera factura.
 * Eso último no es exacto: el facturador la crea sola, con prefijo
 * `FAC-{año}-` y numeración de seis dígitos.
 *
 * Lo que sí es cierto —y es lo caro— es que **ese formato queda fijado con
 * la primera factura emitida**. La numeración correlativa no se puede
 * rehacer, y con Veri*Factu el encadenamiento por huella lo cierra del todo.
 * Así que hay exactamente una ventana para decidirlo, y es antes de emitir.
 */

import { useCallback, useEffect, useState } from 'react';
import { hasPermission, useSession } from '../session/session-context.js';
import { ApiError } from '../session/api-error.js';
import { FormSection, SelectField, TextField } from '../forms/fields.js';

interface Series {
  id: string;
  document_kind: string;
  code: string;
  prefix: string;
  year: number | null;
  next_number: string;
  padding: number;
  is_fiscal: boolean;
  is_active: boolean;
}

const KINDS = [
  { value: 'INVOICE', label: 'Facturas' },
  { value: 'CREDIT_NOTE', label: 'Rectificativas' },
];

const KIND_LABEL: Record<string, string> = {
  INVOICE: 'Facturas',
  CREDIT_NOTE: 'Rectificativas',
  QUOTE: 'Presupuestos',
  SALES_ORDER: 'Pedidos',
  DELIVERY_NOTE: 'Albaranes',
};

/** Cómo quedará el primer número con lo que hay escrito. */
function preview(prefix: string, padding: number, next = 1): string {
  return `${prefix}${String(next).padStart(padding, '0')}`;
}

export function InvoiceSeries(): React.ReactNode {
  const { api, user } = useSession();
  const year = new Date().getFullYear();

  const [series, setSeries] = useState<Series[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [kind, setKind] = useState('INVOICE');
  const [code, setCode] = useState(String(year));
  const [prefix, setPrefix] = useState(`FAC-${year}-`);
  const [padding, setPadding] = useState('6');
  const [nextNumber, setNextNumber] = useState('1');
  // Continua (sin ejercicio) o por año. Cambia si la numeración se
  // reinicia cada 1 de enero o se arrastra indefinidamente.
  const [perYear, setPerYear] = useState(true);

  const canCreate = hasPermission(user, 'billing.create');

  const reload = useCallback(async () => {
    const data = await api.get<{ items: Series[] }>('/billing/series').catch(() => ({ items: [] }));
    setSeries(data.items);
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function create(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.post('/billing/series', {
        documentKind: kind,
        code,
        prefix,
        year: perYear ? year : null,
        padding: Number(padding),
        nextNumber: Number(nextNumber),
      });
      setNotice(
        `Serie creada. La primera factura será ${preview(prefix, Number(padding), Number(nextNumber))}.`,
      );
      await reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se ha podido crear la serie.');
    } finally {
      setBusy(false);
    }
  }

  const fiscal = series.filter((s) => s.is_fiscal);
  const used = fiscal.some((s) => Number(s.next_number) > 1);

  return (
    <section className="panel">
      <h3 className="panel__title">Series de facturación</h3>

      {error !== null && (
        <p className="screen__error" role="alert">
          {error}
        </p>
      )}
      {notice !== null && (
        <p className="screen__note screen__note--strong" role="status">
          {notice}
        </p>
      )}

      {fiscal.length === 0 ? (
        <p className="screen__note screen__note--strong">
          No hay ninguna serie definida. Si emite una factura sin definirla, el sistema creará una
          con el formato <code>{preview(`FAC-${year}-`, 6)}</code> y{' '}
          <strong>ese formato ya no se podrá cambiar</strong>: la numeración correlativa no se
          rehace, y con Veri*Factu el encadenamiento por huella lo cierra del todo.
        </p>
      ) : used ? (
        <p className="screen__note">
          Ya se han emitido facturas. El formato de las series existentes está comprometido y no se
          puede cambiar sin romper la correlatividad.
        </p>
      ) : (
        <p className="screen__note">
          Definidas y todavía sin usar: aún se está a tiempo de cambiarlas.
        </p>
      )}

      {series.length > 0 && (
        <div className="scroller">
          <table className="table__grid table__grid--compact">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Serie</th>
                <th>Ejercicio</th>
                <th>Próximo número</th>
                <th>Fiscal</th>
              </tr>
            </thead>
            <tbody>
              {series.map((s) => (
                <tr key={s.id}>
                  <td>{KIND_LABEL[s.document_kind] ?? s.document_kind}</td>
                  <td>
                    <code>{preview(s.prefix, s.padding, Number(s.next_number))}</code>
                  </td>
                  <td>{s.year ?? '—'}</td>
                  <td>{s.next_number}</td>
                  <td>
                    {s.is_fiscal ? (
                      <span className="pill pill--ok">✓ Sí</span>
                    ) : (
                      <span className="pill pill--off">No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canCreate && (
        <div className="form">
          <FormSection title="Definir serie">
            <SelectField label="Tipo de documento" value={kind} onChange={setKind} options={KINDS} />
            <TextField
              label="Código de serie"
              value={code}
              onChange={setCode}
              help="Lo que identifica la serie internamente. Por ejercicio suele ser el año."
            />
            <TextField
              label="Prefijo"
              value={prefix}
              onChange={setPrefix}
              help="Lo que va delante del número en la factura."
            />
            <TextField
              label="Dígitos del número"
              type="number"
              value={padding}
              onChange={setPadding}
              help="Seis admite un millón de facturas por ejercicio."
            />
            <SelectField
              label="Numeración"
              value={perYear ? 'YEAR' : 'CONTINUOUS'}
              onChange={(v) => setPerYear(v === 'YEAR')}
              options={[
                { value: 'YEAR', label: 'Se reinicia cada ejercicio' },
                { value: 'CONTINUOUS', label: 'Continua, sin reiniciar' },
              ]}
              help="Si se reinicia, hay que definir una serie por año."
            />
            <TextField
              label="Primer número"
              type="number"
              value={nextNumber}
              onChange={setNextNumber}
              help="Para migrar de otro programa: si el anterior llegó a la 508, aquí va 509. La correlatividad del ejercicio se mantiene."
            />
            <p className="screen__note">
              La primera factura será{' '}
              <code>{preview(prefix, Number(padding) || 1, Number(nextNumber) || 1)}</code>
            </p>
            <button
              className="button button--primary"
              type="button"
              disabled={busy || prefix === '' || code === ''}
              onClick={() => void create()}
            >
              {busy ? 'Creando…' : 'Definir serie'}
            </button>
          </FormSection>
        </div>
      )}
    </section>
  );
}
